import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/brand/Reveal";
import { BrandButton } from "@/components/brand/BrandButton";
import { BrandCard } from "@/components/brand/BrandCard";
import { ShareButtons } from "@/components/brand/ShareButtons";
import { ScreenshotFrame } from "@/components/brand/ScreenshotFrame";
import { cn } from "@/lib/utils";
import { getPostBySlug, getRelatedPosts } from "@/content/blogPosts";
import { SITE_NAME, SITE_URL, PUBLISHER, OG_IMAGE, BLOG_CTA_HREF } from "@/content/siteConfig";

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/404" replace />;
  }

  const otherPosts = getRelatedPosts(post, 2);

  return (
    <>
      <SEO
        title={`${post.metaTitle || post.title} - ${SITE_NAME}`}
        description={post.description}
        path={`/blog/${post.slug}`}
        ogType="article"
        image={post.ogImage}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            datePublished: post.date,
            dateModified: post.dateModified,
            image: `${SITE_URL}${OG_IMAGE}`,
            author: { "@type": "Organization", name: PUBLISHER },
            publisher: { "@type": "Organization", name: PUBLISHER },
            mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
              { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_URL}/blog/${post.slug}` },
            ],
          },
          ...(post.faqs
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: post.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.question,
                    acceptedAnswer: { "@type": "Answer", text: f.answer },
                  })),
                },
              ]
            : []),
        ]}
      />

      <article className="mx-auto max-w-2xl px-6 pt-14 pb-20 sm:pt-20 sm:pb-28">
        <Link to="/blog" className="text-sm font-semibold text-cvlink" data-testid="back-to-blog-link">
          &lt;- Back to blog
        </Link>

        <p className="mt-8 text-sm text-cvmuted">
          {new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} - {post.readTime}
        </p>
        <Reveal className="mt-3 font-heading text-3xl leading-tight sm:text-4xl" as="h1">{post.title}</Reveal>

        {post.ogImage && (
          <Reveal className="mt-8">
            <img
              src={post.ogImage}
              alt={post.title}
              width={1200}
              height={630}
              data-testid="article-hero-image"
              className="w-full rounded-[20px] border border-cvborder"
            />
          </Reveal>
        )}

        {post.heroScreenshot && (
          <Reveal className="mt-8">
            <div data-testid="article-hero-screenshot" className="relative overflow-hidden rounded-[20px] border border-cvborder bg-canvas px-8 py-10 sm:px-12 sm:py-14">
              <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-periwinkle/20" aria-hidden="true" />
              <div className="absolute -bottom-20 -left-10 h-52 w-52 rounded-full bg-periwinkle/10" aria-hidden="true" />
              <ScreenshotFrame
                src={post.heroScreenshot}
                alt={post.heroScreenshotAlt || post.title}
                className="relative z-10 mx-auto max-w-[220px]"
              />
            </div>
          </Reveal>
        )}

        {post.shortAnswer && (
          <Reveal className="mt-8">
            <BrandCard data-testid="article-short-answer" className="!p-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-cvmuted">Short answer</p>
              <ul className="mt-3 flex flex-col gap-2">
                {post.shortAnswer.map((line, i) => (
                  <li key={i} className="flex gap-2 text-sm text-cvbody sm:text-base">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-periwinkle" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </BrandCard>
          </Reveal>
        )}

        <div className="mt-10 flex flex-col gap-6">
          {post.body.map((block, i) => {
            if (block.type === "h2") {
              return (
                <Reveal key={i} delay={Math.min(i * 0.02, 0.15)}>
                  <h2 className="font-heading text-2xl">{block.text}</h2>
                </Reveal>
              );
            }
            if (block.type === "cta") {
              return (
                <Reveal key={i} delay={Math.min(i * 0.02, 0.15)}>
                  <BrandCard className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="flex-1 text-base font-semibold text-cvbody">{block.text}</p>
                    <BrandButton to={BLOG_CTA_HREF} className="flex-shrink-0 whitespace-nowrap" data-testid={`article-cta-${i}`}>
                      {block.buttonLabel}
                    </BrandButton>
                  </BrandCard>
                </Reveal>
              );
            }
            if (block.type === "table") {
              return (
                <Reveal key={i} delay={Math.min(i * 0.02, 0.15)}>
                  <BrandCard data-testid="article-age-table" className="overflow-x-auto !p-0">
                    <table className="w-full text-left text-sm sm:text-base">
                      <thead>
                        <tr className="border-b border-cvborder">
                          {block.headers.map((h, hi) => (
                            <th key={hi} className="px-5 py-4 font-heading text-base font-semibold text-ink sm:text-lg">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {block.rows.map((row, ri) => (
                          <tr key={ri} className={ri !== block.rows.length - 1 ? "border-b border-cvborder" : ""}>
                            {row.map((cell, ci) => (
                              <td key={ci} className={cn("px-5 py-4 align-top", ci === 0 ? "font-semibold text-cvbody whitespace-nowrap" : "text-cvbody")}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </BrandCard>
                </Reveal>
              );
            }
            return (
              <Reveal key={i} delay={Math.min(i * 0.02, 0.15)}>
                <p className="text-base text-cvbody sm:text-lg">{block.text}</p>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-12">
          <ShareButtons url={`${SITE_URL}/blog/${post.slug}`} title={post.title} />
        </Reveal>

        <Reveal className="mt-10 border-t border-cvborder pt-10">
          <p className="text-sm font-semibold text-cvmuted">Related reading</p>
          <ul className="mt-4 flex flex-col gap-3">
            {otherPosts.map((p) => (
              <li key={p.slug}>
                <Link to={`/blog/${p.slug}`} className="text-sm font-semibold text-cvlink" data-testid={`related-post-link-${p.slug}`}>
                  {p.title}
                </Link>
              </li>
            ))}
            <li>
              <Link to={post.relatedProductPath} className="text-sm font-semibold text-cvlink" data-testid="article-product-link">
                {post.relatedProductLabel}
              </Link>
            </li>
            {post.externalSources?.map((src, i) => (
              <li key={src.url}>
                <a
                  href={src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-cvlink"
                  data-testid={`article-external-source-${i}`}
                >
                  {src.label} &#8599;
                </a>
              </li>
            ))}
          </ul>
        </Reveal>
      </article>
    </>
  );
}
